// Client-side state machine. Drives which UI panel is visible.
// Embodies the "grammar IS the game" rule: panels light up/disappear based
// on the current phase, never a static always-on HUD.

import type { CharacterSummary, DialogueViewMsg, OwnedItemView, RoomViewMsg, ServerMsg } from "../shared/protocol.js";

export interface StatsView {
  level: number;
  xp: number;
  hp: number;
  hp_max: number;
  mp: number;
  mp_max: number;
}

export interface ReputationView {
  hammer: number;
  maleficium: number;
  neutral: number;
  title: string;
}

export interface QuestLogEntry {
  id: number;
  name: string;
  description: string;
  status: string;
  progress: Array<{ kind: string; target: string; have: number; need: number }>;
  ready_to_turn_in: boolean;
}

export interface SkillTreeView {
  class_name: string;
  skills: Array<{ id: string; name: string; description: string; rank: number; max_rank: number }>;
  points_available: number;
}

export interface TradeSide {
  name: string;
  items: Array<{ instance_id: number; name: string; qty: number }>;
  gold: number;
  confirmed: boolean;
}

export interface TradeView {
  trade_id: number;
  you: TradeSide;
  them: TradeSide;
}

export interface CombatAbility {
  id: string;
  name: string;
  description: string;
  cost: number;
  cooldown_rounds: number;
}

export interface CombatView {
  target_id: number;
  target_template_id: number;
  target_name: string;
  target_hp: number;
  target_hp_max: number;
  player_hp: number;
  player_hp_max: number;
  abilities: CombatAbility[];
  log: Array<{ ts: number; text: string; kind: "out" | "in" | "crit" | "ability" | "status" }>;
}

export interface EntitlementsView {
  tier: "free" | "adventurer" | "patron";
  flags: Record<string, number | boolean>;
}

export interface GuildView {
  id: number;
  name: string;
  rank: string;
  members: Array<{ character_id: number; name: string; rank: string }>;
  progress?: { tier: number; tier_name: string; xp: number; unlocked_rooms: string[]; required_members: number; current_members: number; bank_tabs: number; next_tier: null | { tier: number; name: string; required_xp: number; required_members: number } };
}

export interface GuildInvite {
  guild_id: number;
  guild_name: string;
  from: string;
}

export interface ZoneMap {
  zone: string;
  zone_name: string;
  rooms: Array<{ id: string; name: string; x: number; y: number }>;
  edges: Array<{ from: string; to: string }>;
}

export type Phase = "connecting" | "need_auth" | "need_character" | "playing";

export interface ClientState {
  phase: Phase;
  client_id: string | null;
  token: string | null;
  account_id: number | null;
  character_id: number | null;
  characters: CharacterSummary[];
  room: RoomViewMsg | null;
  /** Active dialogue overlay; null when not talking. */
  dialogue: DialogueViewMsg | null;
  /** Last known inventory (refreshed on inventory msg or after pickup/drop/equip). */
  inventory: OwnedItemView[];
  /** Whether the inventory panel is currently visible. */
  inventory_open: boolean;
  /** Mini-map data for the current zone. */
  zone_map: ZoneMap | null;
  /** Last known character stats (refreshed on stats msg). */
  stats: StatsView | null;
  /** Last known faction reputation. */
  reputation: ReputationView | null;
  /** Last known quest log. */
  quests: QuestLogEntry[];
  /** Last known skill tree. */
  skill_tree: SkillTreeView | null;
  /** Active combat target + rolling log. null when not engaged. */
  combat: CombatView | null;
  /** Active P2P trade session, if any. */
  trade: TradeView | null;
  /** Pending incoming trade requests (other player asked to trade). */
  trade_requests: Array<{ trade_id: number; from: string }>;
  /** Which UI panel is currently open. At most one panel visible at a time. */
  open_panel: null | "character" | "inventory" | "quests" | "skills" | "help";
  cases_list: Array<{ id: number; accuser: string; accused: string; jurisdiction: string; type: string; status: string; current_evidence: number; required_evidence: number; verdict: string; filed_at: number }>;
  craft_skills: Array<{ profession: string; level: number; xp: number; next_level_xp: number }>;
  /** Last few presence/say/system events for the chat log. */
  log: LogEntry[];
  /** Server-declared entitlements. Default: free tier. */
  entitlements: EntitlementsView;
  /** Active character's cosmetic title, if set. */
  title: string | null;
  /** Active character's guild, if any. */
  guild: GuildView | null;
  /** Pending incoming guild invitations. */
  guild_invites: GuildInvite[];
  /** Current moon phase + time to next quarter. */
  moon: { phase: string; glyph: string; seconds_until_next: number; cycle_seconds: number };
  /** Hammer Token balance + stipend state. */
  tokens: { balance: number; lifetime_earned: number; days_until_next_grant: number };
  /** Subscription tenure (paid days + milestone progress). */
  tenure: {
    paid_days: number;
    claimed: Array<{ id: number; days: number; name: string; reward: string }>;
    unclaimed: Array<{ id: number; days: number; name: string; reward: string }>;
    upcoming: Array<{ id: number; days: number; name: string; reward: string; days_remaining: number }>;
  };
  /** Last fetched mail list (inbox or sent, depending on last command). */
  mail: {
    messages: Array<{ id: number; sender: string; subject: string; body: string; attached_gold: number; cod_gold: number; sent_at: number; read_at: number; expires_at: number; direction: "inbox" | "sent" }>;
    count: number;
    capacity: number;
  };
  /** Last fetched auction listings (browse or mine). */
  auction: {
    listings: Array<{ id: number; seller: string; template_id: number; item_name: string; qty: number; asking_gold: number; buyout_gold: number; has_reserve: boolean; reserve_met: boolean; current_bid: number; listed_at: number; expires_at: number; is_mine: boolean; status: string }>;
    my_active: number;
    my_capacity: number;
  };
  /** Friends + block lists + pending inbound request notifications. */
  friends: Array<{ char_id: number; name: string; status: "pending" | "accepted"; direction: "outgoing" | "incoming" | "mutual"; online: boolean; zone: string }>;
  blocks: Array<{ char_id: number; name: string; blocked_at: number; reason: string }>;
  pending_friend_requests: Array<{ from_char_id: number; from_name: string; ts: number }>;
  /** House tier + furniture + guests for the cottage/house panel. */
  house: null | { tier: "cottage" | "manor" | "keep" | "castle"; tier_name: string; description: string; placeable_bonus: number; guest_cap: number; next: null | { tier: "cottage" | "manor" | "keep" | "castle"; name: string; cost_gold: number; cost_tokens: number; min_tenure_days: number; has_gold: boolean; has_tokens: boolean; has_tenure: boolean } };
  cottage: { furniture: Array<{ id: number; name: string; note: string; template_id: number; placed_at: number }>; placed: number; capacity: number; guests: Array<{ character_id: number; name: string; added_at: number; expires_at: number }> };
  /** Per-character aliases loaded from server. Key = lowercase trigger word. */
  aliases: Record<string, string>;
  /** Patron library reader state. */
  library: {
    view: "catalog" | "reading";
    locked: string | null;  // "patron_only" | "login_required" | null (has access)
    books: Array<{ id: number; slug: string; title: string; author: string; year: string; page_count: number; last_page: number }>;
    page: null | { book: { id: number; slug: string; title: string; author: string; year: string; page_count: number; last_page: number }; page_no: number; title: string; body: string; has_prev: boolean; has_next: boolean };
    searchResults: Array<{ book_id: number; book_title: string; page_no: number; page_title: string; snippet: string }>;
  };
}

export type LogTab = "all" | "events" | "combat" | "npc" | "chat";

export interface LogEntry {
  ts: number;
  kind: "system" | "say" | "presence" | "denied" | "echo" | "command";
  text: string;
  who?: string;
  /** Tab this entry belongs to (for the tabbed log). Auto-assigned if not set. */
  tab?: LogTab;
  /** If set, this entry is rendered in Voynichese — font variant + quality tag. */
  voyn_font?: string;
  voyn_quality?: number;
}

/** Classify a log entry into a tab based on kind + content. */
export function classifyLogTab(entry: LogEntry): LogTab {
  if (entry.tab) return entry.tab;
  // Combat
  if (entry.kind === "echo" && /damage|attack|hit|miss|kill|heal|flee|crit|block|dodge|ability/i.test(entry.text)) return "combat";
  if (entry.kind === "denied" && /combat|dead|cooldown|not_enough_mp/i.test(entry.text)) return "combat";
  // Chat (player-to-player)
  if (entry.kind === "say") return entry.who ? "chat" : "npc";
  if (entry.kind === "echo" && /\[global\]|\[trade\]|\[lfg\]|\[party\]|\[guild\]|\[faction\]/i.test(entry.text)) return "chat";
  if (entry.kind === "command") return "chat";
  // NPC
  if (entry.kind === "echo" && /npc|dialogue|quest|storylet|chatter|crier|bard|ballad/i.test(entry.text)) return "npc";
  if (entry.kind === "presence") return "events";
  // Events
  if (entry.kind === "system" && /event|season|weather|plague|outbreak|rumor|curfew|moon/i.test(entry.text)) return "events";
  // Default: all/system
  return "all";
}

export type Listener = (s: ClientState) => void;

export class ClientStore {
  private state: ClientState = {
    phase: "connecting", client_id: null, token: null, account_id: null,
    character_id: null, characters: [], room: null, dialogue: null,
    inventory: [], inventory_open: false, zone_map: null,
    stats: null, reputation: null, quests: [], skill_tree: null,
    combat: null,
    trade: null, trade_requests: [],
    open_panel: null, log: [], cases_list: [], craft_skills: [],
    entitlements: { tier: "free", flags: {} },
    title: null, guild: null, guild_invites: [],
    tokens: { balance: 0, lifetime_earned: 0, days_until_next_grant: -1 },
    moon: { phase: "new", glyph: "◐", seconds_until_next: 120, cycle_seconds: 480 },
    tenure: { paid_days: 0, claimed: [], unclaimed: [], upcoming: [] },
    mail: { messages: [], count: 0, capacity: 0 },
    auction: { listings: [], my_active: 0, my_capacity: 0 },
    friends: [], blocks: [], pending_friend_requests: [],
    house: null,
    cottage: { furniture: [], placed: 0, capacity: 0, guests: [] },
    aliases: {},
    library: { view: "catalog", locked: null, books: [], page: null, searchResults: [] },
  };
  private listeners = new Set<Listener>();

  get(): ClientState { return this.state; }
  subscribe(l: Listener): () => void { this.listeners.add(l); return () => this.listeners.delete(l); }

  private update(patch: Partial<ClientState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l(this.state);
  }

  log(entry: Omit<LogEntry, "ts">): void {
    const log = [...this.state.log, { ts: Date.now(), ...entry }];
    if (log.length > 250) log.splice(0, log.length - 250);
    this.update({ log });
  }

  /** Apply a server message to the state, returning whether the phase changed. */
  apply(msg: ServerMsg): void {
    switch (msg.t) {
      case "hello":
        this.update({ client_id: msg.client_id });
        if (this.state.phase === "connecting") {
          // If we have a saved token, the caller will send a resume; otherwise need_auth.
          this.update({ phase: this.state.token ? "connecting" : "need_auth" });
        }
        break;
      case "session":
        this.update({ token: msg.token, account_id: msg.account_id, character_id: msg.character_id });
        try { localStorage.setItem("hoo_token", msg.token); } catch { /* SSR-safe */ }
        break;
      case "characters":
        this.update({
          characters: msg.list,
          phase: msg.list.length > 0 ? "need_character" : "need_character",
        });
        break;
      case "character_select_required":
        this.update({ phase: "need_character" });
        break;
      case "character_created":
        this.update({ characters: [...this.state.characters, msg.character] });
        break;
      case "spawned":
        this.update({ character_id: msg.character_id, phase: "playing" });
        break;
      case "room":
        this.update({ room: msg.view });
        break;
      case "say":
        this.log({ kind: "say", who: msg.who, text: msg.text });
        break;
      case "emote":
        // Emotes render italicized; reuse "say" bucket with an "* " prefix
        // so the chat view styles them as action text.
        this.log({ kind: "say", text: `* ${msg.text}` });
        break;
      case "emotes_list":
        this.log({ kind: "system", text: `Available emotes: ${msg.keys.join(", ")}` });
        break;
      case "alias_list": {
        const map: Record<string, string> = {};
        for (const a of msg.aliases) map[a.name.toLowerCase()] = a.expansion;
        this.update({ aliases: map });
        break;
      }
      case "channel_list":
        this.log({ kind: "system", text: `Subscribed channels: ${msg.subscribed.join(", ") || "(none)"}` });
        if (msg.volunteer.length) this.log({ kind: "system", text: `Volunteer for: ${msg.volunteer.join(", ")}` });
        break;
      case "who": {
        const players = (msg as any).players as Array<{ name: string; level: number; class: string; faction: string; zone: string }>;
        this.log({ kind: "system", text: `── Online Players (${players.length}) ──` });
        for (const p of players) {
          this.log({ kind: "system", text: `  ${p.name.padEnd(20)} Lv${String(p.level).padEnd(4)} ${p.class.padEnd(16)} ${p.faction.padEnd(12)} ${p.zone}` });
        }
        if (players.length === 0) this.log({ kind: "system", text: "  No one else is online." });
        break;
      }
      case "bestiary": {
        this.log({ kind: "system", text: `Bestiary (${msg.entries.length} species seen, ${msg.total_kills} lifetime kills):` });
        for (const e of msg.entries.slice(0, 30)) {
          this.log({ kind: "system", text: `  ${e.name.padEnd(24)} species=${e.species.padEnd(12)} seen=${e.encounters}  kills=${e.kills}` });
        }
        if (msg.entries.length > 30) this.log({ kind: "system", text: `  … and ${msg.entries.length - 30} more.` });
        break;
      }
      case "honours": {
        const t = msg.totals;
        this.log({ kind: "system", text: `── Honours for ${msg.character_name} ──` });
        this.log({ kind: "system", text: `  ${t.title ? t.title + " " : ""}${t.faction} · ${t.tier} · level ${t.level}${t.guild ? " · ⚑ " + t.guild : ""}` });
        this.log({ kind: "system", text: `  Kills: ${t.kills} · Duels: ${t.duels_won}W/${t.duels_lost}L · Quests: ${t.quests_completed} · Achievements: ${t.achievements}` });
        this.log({ kind: "system", text: `  Purse: ${t.gold}g · Tokens: ${t.tokens}` });
        break;
      }
      case "seasons":
        this.log({ kind: "system", text: `── Active seasons ──` });
        for (const s of msg.active) this.log({ kind: "system", text: `  ${s.name} — ${s.description} (ends ${new Date(s.ends_at).toISOString().slice(0, 10)})` });
        if (!msg.active.length) this.log({ kind: "system", text: "  (none)" });
        this.log({ kind: "system", text: "── Upcoming ──" });
        for (const s of msg.upcoming.slice(0, 5)) this.log({ kind: "system", text: `  ${s.name.padEnd(18)} starts ${new Date(s.starts_at).toISOString().slice(0, 10)}` });
        break;
      case "season_heralded":
        this.log({ kind: "system", text: `✦ ${msg.herald}` });
        break;
      case "world_events":
        this.log({ kind: "system", text: `── Active world events (${msg.active.length}) ──` });
        for (const e of msg.active) {
          const endIso = new Date(e.ends_at).toISOString().slice(11, 16);
          this.log({ kind: "system", text: `  [${e.zone_id}] ${e.name} — ${e.description} (ends ${endIso}Z, kills=${e.kills_credited}, bonus +${e.loot_bonus_gold}g/kill)` });
        }
        if (!msg.active.length) this.log({ kind: "system", text: "  (nothing happening)" });
        break;
      case "world_event_heralded":
        this.log({ kind: "system", text: msg.kind === "start" ? `✦ ${msg.herald}` : `☾ ${msg.herald}` });
        break;
      case "storylet_offer":
        this.log({ kind: "system", text: `── ${msg.title} ──` });
        this.log({ kind: "system", text: msg.prompt });
        for (const c of msg.choices) this.log({ kind: "system", text: `  /choose ${msg.storylet_id} ${c.id}  — ${c.text}` });
        break;
      case "storylet_resolved":
        this.log({ kind: "system", text: msg.result_text });
        break;
      case "lfg_status":
        this.log({ kind: "system", text: `── LFG queues ──` });
        for (const q of msg.queues) this.log({ kind: "system", text: `  ${q.activity_id.padEnd(18)} ${q.name.padEnd(22)} L${q.min_level}+  party=${q.party_size}  queued=${q.queued_count}` });
        if (msg.queued_activity) this.log({ kind: "system", text: `You are queued for ${msg.queued_activity}.` });
        else this.log({ kind: "system", text: "/lfg join <activity_id> to queue." });
        break;
      case "lfg_matched":
        this.log({ kind: "system", text: `✦ LFG matched: ${msg.activity_name} — party with ${msg.character_names.join(", ")}` });
        break;
      case "dungeon_list":
        this.log({ kind: "system", text: `── Dungeons ──` });
        for (const d of msg.entries) {
          const locked = d.locked_until ? ` [locked until ${new Date(d.locked_until).toISOString().slice(0, 16)}]` : "";
          this.log({ kind: "system", text: `  ${d.id.padEnd(20)} ${d.name.padEnd(28)} L${d.min_level}+ party=${d.party_min}-${d.party_max} reward=${d.reward_gold}g${locked}` });
        }
        this.log({ kind: "system", text: "/dungeon enter <id> to begin." });
        break;
      case "dungeon_entered":
        this.log({ kind: "system", text: `★ Entered ${msg.dungeon_id}. Boss: ${msg.boss_name}. Fight!` });
        break;
      case "dungeon_cleared":
        this.log({ kind: "system", text: `★ ${msg.dungeon_id} cleared. +${msg.reward_gold}g +${msg.reward_xp}xp +${msg.reward_tokens}tkn. Locked ${new Date(msg.lockout_until).toISOString().slice(0, 16)}.` });
        break;
      case "dungeon_lockouts":
        this.log({ kind: "system", text: "── Dungeon lockouts ──" });
        for (const l of msg.lockouts) this.log({ kind: "system", text: `  ${l.dungeon_id}  until ${new Date(l.expires_at).toISOString().slice(0, 16)}` });
        if (!msg.lockouts.length) this.log({ kind: "system", text: "  (no lockouts)" });
        break;
      case "raid_list":
        this.log({ kind: "system", text: "── Raid bosses ──" });
        for (const r of msg.entries) {
          const status = r.active_instance ? "ACTIVE NOW" : "dormant";
          const locked = r.locked_until ? ` [locked until ${new Date(r.locked_until).toISOString().slice(0, 10)}]` : "";
          this.log({ kind: "system", text: `  ${r.id.padEnd(16)} ${r.name.padEnd(24)} ${r.zone_id.padEnd(14)} ${status}${locked}` });
        }
        break;
      case "raid_heralded":
        this.log({ kind: "system", text: `✦ ${msg.herald}` });
        break;
      case "raid_cleared":
        this.log({ kind: "system", text: `★ Raid cleared: ${msg.raid_id}. Top damagers:` });
        for (const p of msg.participants.slice(0, 5)) this.log({ kind: "system", text: `  ${p.character_name.padEnd(18)} ${p.damage_dealt}dmg  +${p.reward_gold}g +${p.reward_xp}xp` });
        break;
      case "gather_result":
        this.log({ kind: "system", text: `✦ Gathered ${msg.amount}× ${msg.resource_name} (${msg.profession}). +${msg.xp_gained}xp${msg.leveled_up ? ` — LEVEL UP! (L${msg.new_level})` : ""}` });
        break;
      case "gather_skills":
        this.log({ kind: "system", text: "── Gathering professions ──" });
        for (const s of msg.skills) this.log({ kind: "system", text: `  ${s.profession.padEnd(12)} L${s.level}  ${s.xp}/${s.next_level_xp}xp` });
        break;
      case "craft_result":
        this.log({ kind: "system", text: `✦ Crafted ${msg.amount}× ${msg.recipe_name}. +${msg.xp_gained}xp${msg.leveled_up ? ` — LEVEL UP! (L${msg.new_level})` : ""}` });
        break;
      case "craft_recipes":
        this.update({ craft_recipes: msg.recipes } as Partial<ClientState>);
        break;
      case "craft_skills":
        this.update({ craft_skills: msg.skills });
        this.log({ kind: "system", text: "── Crafting professions ──" });
        for (const s of msg.skills) this.log({ kind: "system", text: `  ${s.profession.padEnd(12)} L${s.level}  ${s.xp}/${s.next_level_xp}xp` });
        break;
      case "divination":
        this.log({ kind: "echo", text: (msg as { npc_action?: string }).npc_action ?? "" });
        this.log({ kind: "echo", text: `The marks form ${(msg as { figure: string }).figure.replace(/_/g, " ")} in the ${(msg as { house: number }).house}${(msg as { house: number }).house === 1 ? "st" : (msg as { house: number }).house === 2 ? "nd" : (msg as { house: number }).house === 3 ? "rd" : "th"} house.` });
        this.log({ kind: "echo", text: (msg as { reading_text: string }).reading_text });
        break;
      case "divination_cooldown":
        this.log({ kind: "system", text: `The spirits are not ready. Try again in ${Math.ceil((msg as { remaining_ms: number }).remaining_ms / 60000)} minutes.` });
        break;
      case "market_book":
        this.log({ kind: "system", text: `── Order book (template #${msg.template_id}) ──` });
        this.log({ kind: "system", text: "  SELLS (low→high):" });
        for (const s of msg.sells.slice(0, 10)) this.log({ kind: "system", text: `    #${s.order_id}  ${s.qty}× @ ${s.price}g  — ${s.seller_char_name}` });
        this.log({ kind: "system", text: "  BUYS (high→low):" });
        for (const b of msg.buys.slice(0, 10)) this.log({ kind: "system", text: `    #${b.order_id}  ${b.qty}× @ ${b.price}g  — ${b.seller_char_name}` });
        break;
      case "market_placed":
        this.log({ kind: "system", text: `✦ Order #${msg.order_id} placed: ${msg.side} ${msg.qty}× @ ${msg.price}g.` });
        break;
      case "market_filled":
        this.log({ kind: "system", text: `✦ Market fill (${msg.your_role}): ${msg.qty}× ${msg.template_name} @ ${msg.price_per_unit}g (total ${msg.total_gold}g) with ${msg.counterparty_name}.` });
        break;
      case "market_mine":
        this.log({ kind: "system", text: `── Your open orders (${msg.orders.length}) ──` });
        for (const o of msg.orders) this.log({ kind: "system", text: `  #${o.order_id}  ${o.side.toUpperCase()}  ${o.qty}/${o.original_qty}× ${o.template_name} @ ${o.price}g` });
        break;
      case "market_history":
        this.log({ kind: "system", text: `── Trade history: ${msg.template_name} (last ${msg.trades.length}) ──` });
        for (const t of msg.trades.slice(0, 10)) {
          this.log({ kind: "system", text: `  ${new Date(t.executed_at).toISOString().slice(11, 16)}  ${t.qty}× @ ${t.price}g — ${t.seller} → ${t.buyer}` });
        }
        break;
      case "market_trend":
        this.log({ kind: "system", text: `── Price trend: ${msg.template_name} ──` });
        for (const d of msg.days) this.log({ kind: "system", text: `  ${d.day}  avg=${d.avg_price}g  vol=${d.volume}` });
        break;
      case "craft_order_list":
        this.log({ kind: "system", text: `── Craft orders (${msg.filter}, ${msg.orders.length}) ──` });
        for (const o of msg.orders.slice(0, 15)) {
          const ing = o.ingredients.map((i) => `${i.qty}× ${i.name}`).join(" + ");
          this.log({ kind: "system", text: `  #${o.id}  [${o.status}]  ${o.output_qty}× ${o.output_name} bounty=${o.bounty_gold}g  ing=${ing}  client=${o.client}${o.committed_by_name ? ` (by ${o.committed_by_name})` : ""}` });
        }
        break;
      case "craft_order_update":
        this.log({ kind: "system", text: `✦ ${msg.message}` });
        break;
      case "shop_browse":
        this.log({ kind: "system", text: `── Shops here (${msg.shops.length}) ──` });
        for (const s of msg.shops) {
          this.log({ kind: "system", text: `  "${s.name}" by ${s.owner}` });
          for (const st of s.stock_preview) this.log({ kind: "system", text: `    #${st.stock_id}  ${st.qty}× ${st.name}  @ ${st.price}g` });
        }
        if (!msg.shops.length) this.log({ kind: "system", text: "  (no shops here)" });
        break;
      case "shop_mine":
        if (!msg.shop) { this.log({ kind: "system", text: "You have no shop. /shop open <till> <name>" }); break; }
        this.log({ kind: "system", text: `── Your shop: "${msg.shop.name}" [${msg.shop.status}] — ${msg.shop.zone_id}/${msg.shop.room_id} ──` });
        this.log({ kind: "system", text: `  Till: ${msg.shop.till_gold}g` });
        for (const s of msg.shop.stock) this.log({ kind: "system", text: `    #${s.stock_id}  ${s.qty}× ${s.name} @ ${s.price}g` });
        break;
      case "shop_transaction":
        if (msg.role === "buyer") this.log({ kind: "system", text: `✦ Bought ${msg.qty}× ${msg.template_name} for ${msg.total_gold}g from ${msg.counterparty}.` });
        else this.log({ kind: "system", text: `✦ Sold ${msg.qty}× ${msg.template_name} (+${msg.total_gold}g to till) to ${msg.counterparty}.` });
        break;
      case "library_locked":
        this.update({ library: { ...this.state.library, locked: msg.reason } });
        this.log({ kind: "denied", text: `Library is patron-tier only (${msg.reason}).` });
        break;
      case "library_catalog":
        this.update({ library: { ...this.state.library, view: "catalog", locked: null, books: msg.books, page: null, searchResults: [] } });
        this.log({ kind: "system", text: `── Library (${msg.books.length} books) ──` });
        for (const b of msg.books) {
          const progress = b.last_page > 0 ? ` (p.${b.last_page}/${b.page_count})` : ` (${b.page_count}pp)`;
          this.log({ kind: "system", text: `  #${b.id}  ${b.title}${b.year ? " — " + b.year : ""}${progress}` });
        }
        this.log({ kind: "system", text: "Use /library open <id> or /library search <query>." });
        break;
      case "library_page":
        this.update({ library: { ...this.state.library, view: "reading", locked: null, page: { book: msg.book, page_no: msg.page_no, title: msg.title, body: msg.body, has_prev: msg.has_prev, has_next: msg.has_next } } });
        this.log({ kind: "system", text: `── ${msg.book.title} · page ${msg.page_no}/${msg.book.page_count}${msg.title ? " — " + msg.title : ""} ──` });
        for (const line of msg.body.split(/\n/)) if (line.trim()) this.log({ kind: "system", text: line });
        this.log({ kind: "system", text: `[/library next · /library prev · /library search <q>]` });
        break;
      case "library_search":
        this.update({ library: { ...this.state.library, searchResults: msg.hits } });
        this.log({ kind: "system", text: `── Search "${msg.query}" (${msg.hits.length} hits) ──` });
        for (const h of msg.hits.slice(0, 20)) {
          this.log({ kind: "system", text: `  ${h.book_title} p.${h.page_no}: ${h.snippet}` });
        }
        break;
      case "alliance_list":
        this.log({ kind: "system", text: `── Alliances (${msg.alliances.length}) ──` });
        for (const a of msg.alliances) {
          this.log({ kind: "system", text: `  #${a.id}  ${a.initiator} + ${a.partner}  [${a.status}]` });
        }
        break;
      case "guild_achievements": {
        const unlocked = msg.entries.filter((e) => e.unlocked).length;
        this.log({ kind: "system", text: `── Guild achievements (${unlocked}/${msg.entries.length} unlocked) ──` });
        for (const a of msg.entries) {
          const mark = a.unlocked ? "★" : "·";
          this.log({ kind: "system", text: `  ${mark} ${a.name.padEnd(24)} ${a.progress}/${a.goal}  — ${a.description}` });
        }
        break;
      }
      case "house_theme_list":
        this.log({ kind: "system", text: `── House themes (current: ${msg.current}) ──` });
        for (const t of msg.themes) this.log({ kind: "system", text: `  ${t.id.padEnd(12)} ${t.name}` });
        this.log({ kind: "system", text: "Use /house theme <id> to swap." });
        break;
      case "guild_war_list":
        this.log({ kind: "system", text: `── Guild wars (${msg.wars.length}) ──` });
        for (const w of msg.wars) {
          const score = `${w.aggressor_score}-${w.defender_score}`;
          this.log({ kind: "system", text: `  #${w.id}  ${w.aggressor} vs ${w.defender}  [${w.status}]  score ${score}  — ${w.reason}` });
        }
        break;
      case "pvp_status":
        this.log({ kind: "system", text: `PvP flag: ${msg.flag.toUpperCase()}. PvP zones: ${msg.pvp_zones.join(", ") || "(none configured)"}` });
        break;
      case "arena_rank":
        this.log({ kind: "system", text: `Arena rank: #${msg.rank || "—"}  rating ${msg.rating}  peak ${msg.peak_rating}  ${msg.wins}W/${msg.losses}L  (season ${msg.season_id})` });
        break;
      case "arena_leaderboard":
        this.log({ kind: "system", text: `── Arena ladder (season ${msg.season_id}) ──` });
        for (const e of msg.entries) this.log({ kind: "system", text: `  #${String(e.rank).padStart(2)}  ${e.character_name.padEnd(20)} ${e.rating}  ${e.wins}W/${e.losses}L` });
        break;
      case "bounty_list":
        this.log({ kind: "system", text: `── Bounties (${msg.filter}, ${msg.bounties.length}) ──` });
        for (const b of msg.bounties) {
          this.log({ kind: "system", text: `  #${b.id}  ${b.gold}g on ${b.target.padEnd(16)} by ${b.poster.padEnd(16)} — ${b.reason}` });
        }
        break;
      case "bounty_claimed":
        this.log({ kind: "system", text: `✦ Bounty #${msg.bounty_id} claimed: ${msg.claimer} slew ${msg.target}. +${msg.gold}g` });
        break;
      case "pet_list": {
        if (!msg.pets.length) { this.log({ kind: "system", text: "No pets. /pet catalog to see available companions." }); break; }
        this.log({ kind: "system", text: `── Pets (${msg.pets.length}) ──` });
        for (const p of msg.pets) {
          const star = p.summoned ? "★" : " ";
          const display = p.custom_name ? `${p.custom_name} (${p.name})` : p.name;
          this.log({ kind: "system", text: `  ${star}#${p.id} ${display.padEnd(30)} L${p.level} ${p.hp}/${p.hp_max}hp  dmg${p.damage}  hunger=${p.hunger} loy=${p.loyalty}` });
        }
        break;
      }
      case "pet_catalog": {
        this.log({ kind: "system", text: "── Pet catalog ──" });
        for (const p of msg.catalog) {
          const cost = p.base_hp * 5 + p.base_damage * 20;
          this.log({ kind: "system", text: `  ${p.id.padEnd(12)} ${p.name.padEnd(22)} ${cost}g  hp=${p.base_hp} dmg=${p.base_damage} — ${p.description}` });
        }
        break;
      }
      case "hireling_list": {
        if (!msg.active.length) { this.log({ kind: "system", text: "No hirelings in service. /hire catalog to see available." }); break; }
        this.log({ kind: "system", text: `── Hirelings (${msg.active.length}) ──` });
        for (const h of msg.active) {
          this.log({ kind: "system", text: `  #${h.id} ${h.name.padEnd(18)} reserve=${h.reserve_gold}g  ~${h.days_remaining}d  +${h.damage_bonus}dmg +${h.defense_bonus}def +${h.hp_bonus}hp` });
        }
        break;
      }
      case "hireling_catalog": {
        this.log({ kind: "system", text: "── Hireling catalog ──" });
        for (const h of msg.catalog) this.log({ kind: "system", text: `  ${h.id.padEnd(14)} ${h.name.padEnd(22)} ${h.upkeep_per_day}g/day  +${h.damage_bonus}d +${h.defense_bonus}def +${h.hp_bonus}hp — ${h.description}` });
        this.log({ kind: "system", text: "/hire hire <template_id> <gold_reserve>" });
        break;
      }
      case "mount_list": {
        if (!msg.roster.length) {
          this.log({ kind: "system", text: "No mounts owned yet." });
          break;
        }
        this.log({ kind: "system", text: `── Mounts (${msg.roster.length}) ──` });
        for (const m of msg.roster) {
          const active = m.id === msg.active_id ? " ★" : "";
          const name = m.custom_name ? `${m.custom_name} (${m.name})` : m.name;
          this.log({ kind: "system", text: `  #${m.id}${active}  ${name.padEnd(30)} ${m.tier.padEnd(10)} +${m.speed_bonus}% speed` });
        }
        this.log({ kind: "system", text: "/mount summon <id> · /mount dismiss · /mount rename <id> <name>" });
        break;
      }
      case "transmog_list": {
        if (!msg.bindings.length && !msg.unlocks.length) {
          this.log({ kind: "system", text: "No transmog unlocks yet. Pick up an equippable item to unlock its look." });
          break;
        }
        this.log({ kind: "system", text: `── Transmog (${msg.bindings.length} active, ${msg.unlocks.length} unlocked) ──` });
        for (const b of msg.bindings) this.log({ kind: "system", text: `  ${b.slot.padEnd(16)} → ${b.appearance_name} (#${b.appearance_template_id})` });
        if (msg.unlocks.length) {
          this.log({ kind: "system", text: "Unlocked appearances (use /mog bind <slot> <template_id>):" });
          for (const u of msg.unlocks.slice(0, 25)) this.log({ kind: "system", text: `  #${u.template_id}  ${u.slot.padEnd(12)} ${u.name}` });
          if (msg.unlocks.length > 25) this.log({ kind: "system", text: `  … and ${msg.unlocks.length - 25} more.` });
        }
        break;
      }
      case "leaderboard": {
        this.log({ kind: "system", text: `── Leaderboard: ${msg.category} ──` });
        for (const e of msg.entries) this.log({ kind: "system", text: `  #${e.rank.toString().padStart(2)}  ${e.character_name.padEnd(24)} ${e.value}` });
        break;
      }
      case "dialogue":
        this.update({ dialogue: msg.view });
        break;
      case "dialogue_end":
        this.update({ dialogue: null });
        break;
      case "inventory":
        this.update({ inventory: msg.items, inventory_open: true });
        break;
      case "picked_up":
        this.log({ kind: "system", text: `You pick up ${msg.item.name} (x${msg.item.qty}).` });
        break;
      case "dropped":
        this.log({ kind: "system", text: `You drop ${msg.name} (x${msg.qty}).` });
        break;
      case "zone_map":
        this.update({ zone_map: msg });
        break;
      case "stats":
        this.update({ stats: { level: msg.level, xp: msg.xp, hp: msg.hp, hp_max: msg.hp_max, mp: msg.mp, mp_max: msg.mp_max } });
        break;
      case "reputation":
        this.update({ reputation: { hammer: msg.hammer, maleficium: msg.maleficium, neutral: msg.neutral, title: msg.title } });
        break;
      case "quests":
        this.update({ quests: msg.entries });
        break;
      case "quest_progress": {
        const q = this.state.quests.find((x) => x.id === msg.quest_id);
        if (q) {
          const progress = q.progress.map((p, i) => msg.progress[i] ? { ...p, have: msg.progress[i].have, need: msg.progress[i].need } : p);
          this.update({ quests: this.state.quests.map((x) => x.id === msg.quest_id ? { ...x, progress } : x) });
        }
        this.log({ kind: "system", text: `Quest progress — ${msg.name}` });
        break;
      }
      case "quest_assigned":
        this.log({ kind: "system", text: `Quest accepted — ${msg.name}` });
        break;
      case "quest_completed":
        this.log({ kind: "system", text: `Quest complete — ${msg.name} (+${msg.xp} XP, +${msg.gold} gold)` });
        break;
      case "skill_tree":
        this.update({ skill_tree: { class_name: msg.class_name, skills: msg.skills, points_available: msg.points_available } });
        break;
      case "combat_started":
        this.update({
          combat: {
            target_id: msg.mob_id,
            target_template_id: msg.mob_template_id,
            target_name: msg.mob_name,
            target_hp: msg.mob_hp,
            target_hp_max: msg.mob_hp_max,
            player_hp: this.state.stats?.hp ?? 0,
            player_hp_max: this.state.stats?.hp_max ?? 1,
            abilities: msg.abilities,
            log: [{ ts: Date.now(), text: `You engage ${msg.mob_name}.`, kind: "status" }],
          },
        });
        break;
      case "combat": {
        const r = msg.round;
        const cur = this.state.combat;
        if (cur) {
          const entries = [...cur.log];
          const abilityPrefix = r.ability_name ? `${r.ability_name}: ` : "";
          const critSuffix = r.crit ? " (CRIT!)" : "";
          if (r.attacker_dmg > 0) entries.push({ ts: Date.now(), text: `${abilityPrefix}You hit ${r.mob_name} for ${r.attacker_dmg}${critSuffix}.`, kind: r.crit ? "crit" : "out" });
          if (r.mob_dmg > 0)      entries.push({ ts: Date.now(), text: `${r.mob_name} hits you for ${r.mob_dmg}.`, kind: "in" });
          while (entries.length > 8) entries.shift();
          this.update({
            combat: {
              ...cur,
              target_hp: r.mob_hp,
              target_hp_max: r.mob_hp_max,
              player_hp: r.player_hp,
              player_hp_max: r.player_hp_max,
              log: entries,
            },
          });
        }
        // Also update stats HP view if open
        if (this.state.stats) {
          this.update({ stats: { ...this.state.stats, hp: r.player_hp, hp_max: r.player_hp_max } });
        }
        break;
      }
      case "combat_ended":
        this.update({ combat: null });
        this.log({ kind: "system", text:
          msg.reason === "mob_killed" ? "Combat ends — you are victorious." :
          msg.reason === "you_died" ? "You have fallen in battle." :
          msg.reason === "fled" ? "You escape the fight." :
          msg.reason === "mob_fled" ? "Your target flees." :
          "You disengage.",
        });
        break;
      case "flee_failed":
        if (this.state.combat) {
          const log = [...this.state.combat.log,
            { ts: Date.now(), text: `Flee failed! You take ${msg.counter_dmg} damage.`, kind: "in" as const }];
          while (log.length > 8) log.shift();
          this.update({ combat: { ...this.state.combat, player_hp: msg.player_hp, player_hp_max: msg.player_hp_max, log } });
        }
        break;
      case "trade_request":
        this.update({ trade_requests: [...this.state.trade_requests, { trade_id: msg.trade_id, from: msg.from }] });
        this.log({ kind: "system", text: `${msg.from} wants to trade. /paccept ${msg.trade_id}` });
        break;
      case "trade_window":
        this.update({ trade: { trade_id: msg.trade_id, you: msg.you, them: msg.them } });
        break;
      case "trade_complete":
        this.update({ trade: null });
        this.log({ kind: "system", text: `Trade complete. Received: ${msg.you_received.items.join(", ") || "nothing"} + ${msg.you_received.gold} gold.` });
        break;
      case "trade_cancelled":
        this.update({ trade: null });
        this.log({ kind: "system", text: `Trade cancelled: ${msg.reason}` });
        break;
      case "skill_trained":
        if (this.state.skill_tree) {
          const skills = this.state.skill_tree.skills.map((s) => s.id === msg.skill_id ? { ...s, rank: msg.new_rank } : s);
          this.update({ skill_tree: { ...this.state.skill_tree, skills, points_available: msg.points_remaining } });
        }
        this.log({ kind: "system", text: `Trained ${msg.skill_id} to rank ${msg.new_rank}.` });
        break;
      case "rep_gain":
        if (this.state.reputation) {
          const faction = msg.faction as keyof Omit<ReputationView, "title">;
          const rep = { ...this.state.reputation, [faction]: msg.new_value };
          if (msg.new_title) rep.title = msg.new_title;
          this.update({ reputation: rep });
        }
        this.log({ kind: "system", text: `Reputation with ${msg.faction}: ${msg.delta >= 0 ? "+" : ""}${msg.delta} (now ${msg.new_value}${msg.new_title ? ` — ${msg.new_title}` : ""}).` });
        break;
      case "entitlements":
        this.update({ entitlements: { tier: msg.tier, flags: msg.flags as unknown as Record<string, number | boolean> } });
        this.log({ kind: "system", text: `Subscription tier: ${msg.tier}` });
        break;
      case "custom_emote":
        this.log({ kind: "presence", text: `* ${msg.who} ${msg.action}` });
        break;
      case "title_set":
        this.update({ title: msg.title });
        break;
      case "guild_info":
        this.update({ guild: msg.guild });
        break;
      case "tenure_status":
        this.update({ tenure: { paid_days: msg.paid_days, claimed: msg.claimed, unclaimed: msg.unclaimed, upcoming: msg.upcoming } });
        break;
      case "mail_inbox":
        this.update({ mail: { messages: msg.messages, count: msg.inbox_count, capacity: msg.inbox_capacity } });
        this.log({ kind: "system", text: `Mail: ${msg.inbox_count}/${msg.inbox_capacity}` });
        for (const m of msg.messages.slice(0, 10)) {
          const flags = [
            m.read_at === 0 ? "UNREAD" : "",
            m.attached_gold > 0 ? `+${m.attached_gold}g` : "",
            m.cod_gold > 0 ? `COD ${m.cod_gold}g` : "",
          ].filter(Boolean).join(" ");
          this.log({ kind: "system", text: `  #${m.id} ${m.direction === "sent" ? "→" : "←"} ${m.sender}: ${m.subject}${flags ? " [" + flags + "]" : ""}` });
        }
        break;
      case "mail_sent_ok":
        this.log({ kind: "system", text: `Mail queued (id ${msg.mail_id}).` });
        break;
      case "auction_listings":
        this.update({ auction: { listings: msg.listings, my_active: msg.my_active, my_capacity: msg.my_capacity } });
        this.log({ kind: "system", text: `Auction — ${msg.listings.length} listings shown (yours: ${msg.my_active}/${msg.my_capacity}).` });
        for (const l of msg.listings.slice(0, 15)) {
          const mine = l.is_mine ? " [you]" : "";
          const bid = l.current_bid > 0 ? ` | bid ${l.current_bid}g` : "";
          const bo = l.buyout_gold > 0 ? ` | buyout ${l.buyout_gold}g` : "";
          const rv = l.has_reserve ? (l.reserve_met ? " | reserve: met" : " | reserve: not met") : "";
          this.log({ kind: "system", text: `  #${l.id} ${l.qty}× ${l.item_name} — start ${l.asking_gold}g${bid}${bo}${rv} — ${l.seller}${mine} (${l.status})` });
        }
        break;
      case "auction_listed":
        this.log({ kind: "system", text: `Listed #${msg.listing_id}: ${msg.qty}× ${msg.item_name} @ ${msg.asking_gold}g (fee ${msg.fee_paid}g).` });
        break;
      case "auction_sold":
        this.log({ kind: "system", text: `SOLD — ${msg.buyer} bought your ${msg.qty}× ${msg.item_name} for ${msg.gold_gained}g.` });
        break;
      case "auction_bought":
        this.log({ kind: "system", text: `Bought ${msg.qty}× ${msg.item_name} from ${msg.seller} for ${msg.gold_spent}g.` });
        break;
      case "auction_cancelled":
        this.log({ kind: "system", text: `Listing #${msg.listing_id} ${msg.reason} — ${msg.item_name} returned.` });
        break;
      case "auction_bid_placed":
        this.log({ kind: "system", text: `Bid #${msg.listing_id}: ${msg.bid_gold}g placed. Next min: ${msg.min_next_bid}g.` });
        break;
      case "auction_outbid":
        this.log({ kind: "system", text: `OUTBID on #${msg.listing_id} ${msg.item_name} — new bid ${msg.new_bid}g, ${msg.refund}g refunded.` });
        break;
      case "accusation_update":
        this.log({ kind: "system", text: `Case #${msg.accusation.id} — ${msg.accusation.accuser} v. ${msg.accusation.accused} — ${msg.accusation.type} (${msg.accusation.jurisdiction}) — ${msg.accusation.status} — ${msg.accusation.current_evidence}/${msg.accusation.required_evidence} evidence` });
        break;
      case "accusation_list":
        this.update({ cases_list: msg.cases });
        break;
      case "accusation_detail":
        // Same — handled by server-side system lines.
        break;
      case "accusation_verdict":
        this.log({ kind: "system", text: `VERDICT on case #${msg.accusation_id}: ${msg.verdict.toUpperCase()} — ${msg.accused}${msg.sentence !== "none" ? ` (sentence: ${msg.sentence}${msg.sentence_amount > 0 ? " " + msg.sentence_amount : ""})` : ""}${msg.appeal_deadline > 0 ? ` | appeal within ${Math.ceil((msg.appeal_deadline - Date.now()) / (24 * 3600 * 1000))}d` : ""}` });
        break;
      case "territory_status":
      case "territory_claimed":
      case "territory_contested":
      case "territory_resolved":
        // Server-side system text covers display.
        break;
      case "moon_tick":
        this.update({ moon: { phase: msg.phase, glyph: msg.glyph, seconds_until_next: msg.seconds_until_next, cycle_seconds: msg.cycle_seconds } });
        break;
      case "standing_view":
      case "standing_change":
      case "betrayal_result":
      case "agent_registered":
      case "agent_discovered":
      case "disguise_applied":
      case "disguise_detected":
        // Server-side system text covers display.
        break;
      case "ritual_list":
      case "ritual_started":
      case "ritual_joined":
      case "prayer_list":
      case "prayer_started":
        // System text lines handled server-side; no client state yet.
        break;
      case "primi_recipes":
        // System lines cover list.
        break;
      case "primi_crafted":
        this.log({ kind: "system", text: `Crafted: ${msg.item_name} (quality ${(msg.quality * 100).toFixed(0)}%).` });
        break;
      case "exorcism_started":
        this.log({ kind: "system", text: `EXORCISM #${msg.id} ${msg.exorcist} vs ${msg.target} — ${msg.target_hp} HP / ${msg.exorcist_power} power / ${msg.max_rounds} rounds` });
        break;
      case "exorcism_round":
        this.log({ kind: "system", text: `  round ${msg.round} [${msg.action}]: ${msg.damage} dmg → HP ${msg.target_hp} / power ${msg.exorcist_power}` });
        break;
      case "exorcism_ended":
        this.log({ kind: "system", text: `Exorcism #${msg.id} ${msg.status.toUpperCase()} — ${msg.summary}` });
        break;
      case "prayer_completed":
        this.log({ kind: "system", text: `Prayer #${msg.prayer_id} (${msg.template_id}) ${msg.success ? "SUCCESS" : "FAIL"} — ${msg.success ? msg.effect_summary : msg.reason}` });
        break;
      case "ritual_completed":
        this.log({ kind: "system", text: `Ritual #${msg.ritual_id} (${msg.template_id}) ${msg.success ? "SUCCESS" : "FAIL"} — ${msg.success ? msg.effect_summary : msg.reason}` });
        break;
      case "coven_info":
        // System lines carry the presentation; no state change needed here yet.
        break;
      case "coven_invitation":
        this.log({ kind: "system", text: `${msg.from} invites you to "${msg.coven_name}". /coven accept ${msg.invite_id}` });
        break;
      case "voynich_encoded":
        this.log({ kind: "echo", text: msg.encoded, voyn_font: msg.font_variant, voyn_quality: Math.round(msg.quality * 100) });
        this.log({ kind: "system", text: `(section ${msg.section}, quality ${(msg.quality * 100).toFixed(0)}%)` });
        break;
      case "voynich_decoded":
        this.log({ kind: "system", text: `decode: ${msg.words.map((w) => `${w.raw}(${w.role})`).join(" ")} — confidence ${(msg.confidence * 100).toFixed(0)}%` });
        break;
      case "voynich_cast":
        this.log({ kind: "say", who: msg.caster, text: msg.encoded, voyn_font: msg.font_variant, voyn_quality: Math.round(msg.quality * 100) });
        break;
      case "scribe_mastery":
        // System line comes with it.
        break;
      case "glyph_learned":
        this.log({ kind: "system", text: `Learned "${msg.glyph}" (d${msg.difficulty}). Mastery now ${msg.new_mastery}.` });
        break;
      case "glyphs_known":
        // System line comes with it.
        break;
      case "inquisitor_rank":
        this.log({ kind: "system", text: `Inquisitor rank: ${msg.rank_name} (L${msg.level}, authority ${msg.authority}) — ${msg.investigations_completed} investigations, ${msg.trials_presided} trials` });
        break;
      case "guild_bank_view":
        this.log({ kind: "system", text: `Guild bank — ${msg.items.length} item rows` });
        for (const it of msg.items) {
          this.log({ kind: "system", text: `  #${it.id} tab${it.tab}: ${it.qty}× ${it.name} (from ${it.deposited_by})` });
        }
        if (msg.log.length > 0) {
          this.log({ kind: "system", text: `Recent activity:` });
          for (const l of msg.log.slice(0, 10)) {
            this.log({ kind: "system", text: `  ${l.actor} ${l.action} ${l.qty}× ${l.item_name}` });
          }
        }
        break;
      case "bank_view": {
        this.update({ bank: { tabs: msg.tabs, labels: msg.labels ?? {}, items: msg.items } } as Partial<ClientState>);
        this.log({ kind: "system", text: `Bank — ${msg.tabs} tabs, ${msg.items.length} items` });
        break;
      }
      case "cottage_furniture":
        this.update({ cottage: { ...this.state.cottage, furniture: msg.items, placed: msg.placed, capacity: msg.capacity } });
        break;
      case "cottage_guests_list":
        this.update({ cottage: { ...this.state.cottage, guests: msg.guests } });
        break;
      case "auction_won":
        this.log({ kind: "system", text: `WON #${msg.listing_id}: ${msg.qty}× ${msg.item_name} for ${msg.winning_bid}g.` });
        break;
      case "mail_received":
        this.log({ kind: "system", text: `New mail from ${msg.from}: "${msg.subject}" — /mail inbox to view.` });
        break;
      case "rename_result": {
        const newChars = this.state.characters.map((c) =>
          c.id === msg.character_id ? { ...c, name: msg.new_name } : c
        );
        this.update({ characters: newChars });
        this.log({ kind: "system", text: `Renamed: ${msg.old_name} → ${msg.new_name}` });
        break;
      }
      case "tenure_claimed":
        this.log({ kind: "system", text: `Milestone claimed — ${msg.milestone.name} (${msg.milestone.days}d): ${msg.milestone.reward}` });
        break;
      case "token_balance":
        this.update({ tokens: { balance: msg.balance, lifetime_earned: msg.lifetime_earned, days_until_next_grant: msg.days_until_next_grant } });
        if (msg.last_grant_delta) this.log({ kind: "system", text: `+${msg.last_grant_delta} Hammer Tokens (Patron monthly stipend).` });
        break;
      case "guild_invitation":
        this.update({ guild_invites: [...this.state.guild_invites, { guild_id: msg.guild_id, guild_name: msg.guild_name, from: msg.from }] });
        this.log({ kind: "system", text: `${msg.from} invites you to ${msg.guild_name}. /guild accept ${msg.guild_id}` });
        break;
      case "presence":
        this.log({
          kind: "presence",
          text: msg.kind === "arrive"
            ? `${msg.who} arrives${msg.from ? " from the " + msg.from : ""}.`
            : `${msg.who} leaves${msg.to ? " heading " + msg.to : ""}.`,
        });
        break;
      case "system":
        this.log({ kind: "system", text: msg.text });
        break;
      case "denied": {
        const reason = msg.reason === "rate_limited"
          ? "Slow down — you are sending commands too quickly."
          : msg.reason === "frozen_by_gm"
            ? "Your character has been frozen by a GM."
            : `denied: ${msg.reason}`;
        this.log({ kind: "denied", text: reason });
        break;
      }
      case "echo":
        this.log({ kind: "echo", text: msg.text });
        break;
      case "error":
        this.log({ kind: "denied", text: `error: ${msg.reason}` });
        if (msg.reason === "session_expired") {
          this.update({ phase: "need_auth", token: null });
          try { localStorage.removeItem("hoo_token"); } catch { /* */ }
        }
        break;
      case "friend_list":
        this.update({ friends: msg.friends });
        break;
      case "friend_request_received":
        this.update({
          pending_friend_requests: [
            ...this.state.pending_friend_requests.filter((p) => p.from_char_id !== msg.from_char_id),
            { from_char_id: msg.from_char_id, from_name: msg.from_name, ts: Date.now() },
          ],
        });
        this.log({ kind: "system", text: `${msg.from_name} sent a friend request. Use /friend accept ${msg.from_char_id}` });
        break;
      case "block_list":
        this.update({ blocks: msg.blocks });
        break;
      case "daily_status": {
        const lines: string[] = [];
        lines.push(`Daily rewards — streak day: ${msg.current_streak_day || "(new)"}`);
        for (const c of msg.cycle) {
          const marker = msg.can_claim && msg.claimable_day === c.day ? "→ " : "  ";
          const claimed = c.day <= msg.current_streak_day ? " ✓" : "";
          lines.push(`${marker}day ${c.day}: ${c.label}${claimed}`);
        }
        if (msg.can_claim) lines.push("Type /daily claim to collect.");
        else lines.push(`Next claim in ${Math.floor(msg.seconds_until_next_utc / 3600)}h ${Math.floor((msg.seconds_until_next_utc % 3600) / 60)}m (UTC midnight).`);
        for (const line of lines) this.log({ kind: "system", text: line });
        break;
      }
      case "daily_claimed":
        this.log({ kind: "system", text: `✔ Day ${msg.day} reward: ${msg.label}` });
        break;
      case "daily_quests":
        this.log({ kind: "system", text: `Daily quests (${msg.date}):` });
        for (const q of msg.quests) {
          const status = q.completed ? "DONE" : q.ready ? "READY" : `${q.progress}/${q.goal}`;
          this.log({ kind: "system", text: `  [${status}] ${q.id} — ${q.title}: ${q.objective_text}. Reward: ${q.reward_gold}g ${q.reward_xp}xp` });
        }
        this.log({ kind: "system", text: "Type /dq turn_in <id> when READY." });
        break;
      case "daily_quest_progress":
        this.log({ kind: "system", text: `Daily "${msg.title}": ${msg.progress}/${msg.goal}${msg.ready ? " (READY)" : ""}` });
        break;
      case "daily_quest_completed":
        this.log({ kind: "system", text: `✔ Daily quest "${msg.title}" complete. +${msg.reward_gold}g +${msg.reward_xp}xp` });
        break;
      case "duel_invitation":
        this.log({ kind: "system", text: `${msg.from} challenges you to a duel! /duel accept ${msg.duel_id} | /duel decline ${msg.duel_id}` });
        break;
      case "duel_started":
        this.log({ kind: "system", text: `Duel #${msg.duel_id} begins vs ${msg.opp_name}. HP ${msg.your_hp}/${msg.opp_hp}. Actions: /duel act ${msg.duel_id} <strike|parry|rest>` });
        break;
      case "duel_round":
        this.log({ kind: "system", text: `Duel round ${msg.round}: you ${msg.your_action}, opp ${msg.opp_action}. HP you=${msg.your_hp} opp=${msg.opp_hp}` });
        break;
      case "duel_ended":
        this.log({ kind: "system", text: `Duel ended: ${msg.your_result.toUpperCase()} (winner: ${msg.winner_name}). Record ${msg.wins}W/${msg.losses}L.` });
        break;
      case "duel_record":
        this.log({ kind: "system", text: `Duel record: ${msg.wins}W / ${msg.losses}L` });
        break;
      case "achievements": {
        const byCat: Record<string, typeof msg.entries> = {};
        for (const e of msg.entries) (byCat[e.category] ??= []).push(e);
        const unlocked = msg.entries.filter((e) => e.unlocked).length;
        this.log({ kind: "system", text: `Achievements: ${unlocked}/${msg.entries.length} unlocked.` });
        for (const [cat, arr] of Object.entries(byCat)) {
          this.log({ kind: "system", text: `  ${cat.toUpperCase()}:` });
          for (const e of arr) {
            const mark = e.unlocked ? "★" : "·";
            this.log({ kind: "system", text: `    ${mark} ${e.name} (${e.progress}/${e.goal}) — ${e.description}` });
          }
        }
        break;
      }
      case "achievement_unlocked":
        this.log({ kind: "system", text: `★ UNLOCKED: ${msg.name} — ${msg.description}` });
        break;
      case "tutorial_status":
        if (msg.completed && !msg.reward_claimed) {
          this.log({ kind: "system", text: `Tutorial complete (${msg.step}/${msg.total_steps}). Type /tutorial reward to collect.` });
        } else if (msg.hint) {
          this.log({ kind: "system", text: `Tutorial (${msg.step}/${msg.total_steps}): ${msg.hint}` });
        } else {
          this.log({ kind: "system", text: `Tutorial complete.` });
        }
        break;
      case "tutorial_advanced":
        // Inline hint is already sent as a system message; just mark state.
        break;
      case "tutorial_reward":
        this.log({ kind: "system", text: `✔ Tutorial reward: +${msg.gold}g +${msg.tokens} token +${msg.xp}xp.` });
        break;
      case "house_status":
        this.update({
          house: {
            tier: msg.tier, tier_name: msg.tier_name, description: msg.description,
            placeable_bonus: msg.placeable_bonus, guest_cap: msg.guest_cap,
            next: msg.next,
          },
        });
        break;
      case "house_upgraded":
        this.log({ kind: "system", text: `✔ House upgraded to ${msg.tier_name}! (-${msg.gold_spent}g -${msg.tokens_spent} tokens)` });
        break;
      // heartbeat: handled by HUD, not state
    }
  }

  /** Restore a saved token from localStorage at startup. */
  restoreToken(): string | null {
    try {
      const t = localStorage.getItem("hoo_token");
      if (t) this.update({ token: t });
      return t;
    } catch { return null; }
  }

  reset(): void {
    try { localStorage.removeItem("hoo_token"); } catch { /* */ }
    this.update({
      phase: "need_auth", token: null, account_id: null, character_id: null,
      characters: [], room: null, dialogue: null, inventory: [], inventory_open: false,
    });
  }

  toggleInventory(open?: boolean): void {
    this.update({ inventory_open: open ?? !this.state.inventory_open });
  }

  openPanel(panel: ClientState["open_panel"]): void {
    this.update({ open_panel: panel });
  }

  closePanel(): void {
    this.update({ open_panel: null });
  }

  togglePanel(panel: NonNullable<ClientState["open_panel"]>): void {
    this.update({ open_panel: this.state.open_panel === panel ? null : panel });
  }
}
